import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import { Page, PromiseContainer } from '../../../components';
import { useIsAdmin, useStore } from '../../../store';
import usePromise from '../../../store/utils/usePromise';
import { formatTime } from '../../../utils/date';
import KeyActions from '../components/KeyActions';
import PulseKey from '../store/PulseKey';
import LicenseSection, { alignLeft } from './LicenseSection';

export default observer(function KeyPage() {
  const { key = '' } = useParams();
  const { pulseKeyStore } = useStore();
  const isAdmin = useIsAdmin({ mode: 'any' });
  const [keyPromise, reload] = usePromise(
    () => pulseKeyStore.fetchPulseKey(key),
    [key],
  );

  return (
    <Page navigation={key}>
      <PromiseContainer promise={keyPromise}>
        {({ pulseKey, sellableName }) => (
          <>
            <Table size="small" sx={alignLeft}>
              <TableBody>
                {getDetails(pulseKey, sellableName).map(([name, value]) => (
                  <TableRow
                    key={name}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" sx={{ width: 200 }}>
                      {name}
                    </TableCell>
                    <TableCell>{value || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {isAdmin && pulseKey.license && (
              <LicenseSection
                license={pulseKey.license}
                onChange={reload}
                pulseKey={pulseKey}
              />
            )}

            <Divider sx={{ mt: 2 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              <KeyActions labeled onChange={reload} pulseKey={pulseKey} />
            </Box>
          </>
        )}
      </PromiseContainer>
    </Page>
  );
});

function getDetails(pulseKey: PulseKey, sellableName: string) {
  return [
    ['Product', sellableName],
    ['Product ID', pulseKey.sellableId],
    [
      'Status',
      pulseKey.disabled ? `${pulseKey.status}, disabled` : pulseKey.status,
    ],
    ['Customer', pulseKey.customerEmail],
    ['Label', pulseKey.label],
    ['Creation date', pulseKey.createdAt && formatTime(pulseKey.createdAt)],
    ['Redemption time', pulseKey.redeemedAt && formatTime(pulseKey.redeemedAt)],
    ['Sent date', pulseKey.fulfilledAt && formatTime(pulseKey.fulfilledAt)],
    ['Number of downloads', String(pulseKey.downloadCount ?? 0)],
    ['IP of redemption', pulseKey.redemptionIp],
    ['IP of download', pulseKey.ipOfDownload],
  ] as const;
}
